<template>
  <layout-wrapper class="bg-light-grey" hide-margin is-footer>
    <template #header>
      <van-nav-bar left-arrow @click-left="$router.back()">
        <template #title>
          <h1>{{ blogDetail?.title }}</h1>
        </template>
      </van-nav-bar>
    </template>
    <full-page-loader :loading="loading"/>
    <div class="custom-container mx-auto m-3">
      <div v-if="!loading" class="grid grid-cols-12 gap-5">
        <div class="col-start-0 md:col-start-2 col-span-12 md:col-span-10">
          <bread-crumbs :items="breadCrumbsArray" class="my-1 mb-3 sm:custom-container hidden sm:block"/>
          <background-image :src="blogDetail?.image" class="w-full h-[240px] rounded-t"/>
          <div class="bg-white rounded-md p-3 sm:p-5">
            <template v-if="!isEmpty(blogDetail)">
              <h1 v-if="$device.isDesktop" class="text-2xl font-bold mb-3">{{ blogDetail?.title }}</h1>
              <div class="mb-5 inherit-style" v-html="blogDetail?.description"></div>
              <div class="flex items-center justify-between mt-5 w-full">
                <nuxt-link :class="{'cursor-not-allowed text-grey-muted': !blogDetail?.prev_slug}"
                           :to="$routerUrl(`/blogs/${blogDetail?.prev_slug}`)">
                  <van-icon class="flip-icon" name="arrow-double-left"/>
                  <span class="text-sm sm:text-base">{{ $t('blogs.previous') }}</span>
                </nuxt-link>
                <nuxt-link :class="{'cursor-not-allowed text-grey-muted': !blogDetail?.next_slug}"
                           :to="$routerUrl(`/blogs/${blogDetail?.next_slug}`)">
                  <span class="text-sm sm:text-base">{{ $t('blogs.next') }}</span>
                  <van-icon class="flip-icon" name="arrow-double-right"/>
                </nuxt-link>
              </div>
            </template>
          </div>
          <div v-if="relatedBlogs.length" class="relative">
            <device-wrapper #default="{ desktop }">
              <div class="text-base sm:text-xl font-bold my-3 mx-3 md:mx-0">{{ $t('blogs.related_blogs') }}</div>
              <!-- Desktop product slider -->
              <template v-if="desktop">
                <swiper-wrapper :slides-per-group="5" :slidesPerView="5" :space-between="16" class="relative">
                  <template #swiper>
                    <swiper-slide v-for="(blog, index) in relatedBlogs" :key="`blog-desktop-related-${index}`"
                                  class="bg-white rounded-md">
                      <blogs-list-item :item="blog"/>
                    </swiper-slide>
                  </template>
                </swiper-wrapper>
              </template>
              <!-- Mobile product slider -->
              <template v-else>
                <div class="flex items-center overflow-x-auto ps-3 mb-3">
                  <blogs-list-item v-for="(item, index) in relatedBlogs" :key="`blog-mobile-related-${index}`"
                                   :item="item"
                                   class="min-w-[300px] max-w-[450px] relative me-3"/>
                </div>
              </template>
            </device-wrapper>
          </div>
        </div>
      </div>
    </div>
  </layout-wrapper>
</template>

<script setup>
import SwiperWrapper from "@/components/common/SwiperWrapper";
import {useCustomFetch} from "@/composables/useFetch";
import BreadCrumbs from "@/components/common/BreadCrumbs";
import {useIsEmpty} from "@/composables/useIsEmpty";
import BlogHead from "@/head/blog";
import BackgroundImage from "@/components/common/BackgroundImage";

const {isEmpty} = useIsEmpty()
const route = useRoute();
const {t} = useI18n();
const blogIds = route.params.id.match(/-n[\d+].*/)
const [_, blogId] = blogIds[blogIds?.length - 1]?.split('-')
const id = blogId?.replace('n', '')
const {data, loading} = await useCustomFetch("?route=rest/v4/blogs/detail", {id: id});
const blogDetail = computed(() => data.value?.details);
const relatedBlogs = computed(() => data.value?.related_blogs);
const breadCrumbsArray = computed(() => {
  const mappedArray = [{title: t('blogs.blogs'), link: useNuxtApp().$routerUrl('blogs')}];
  mappedArray.push({
    title: blogDetail.value?.title,
  });
  return mappedArray;
});
useHead({
  titleTemplate: (title) => `${title} | ${t("seo.niceone_ksa")}`,
  title: () => data.value?.details?.title,
  ...BlogHead({blog: data.value?.details}),
});
</script>

<style scoped>
</style>